/**
 * 예약 캘린더 월/주 그리드 날짜 산술(설계 §2-1·§2-7 D1) — 순수 함수, 외부 날짜 라이브러리 없음.
 *
 * 날짜는 항상 "yyyy-MM-dd" 문자열(dateKey)로 다루고, 산술은 `Date.UTC` 로만 한다 — 달력 날짜는
 * 시각이 아니라 순수한 연/월/일이라 로컬 타임존의 DST·자정 경계에 흔들리면 안 된다.
 *
 * 🚨 주 시작 요일은 일요일로 고정한다(설계 §2-7 D9 — Figma·기능명세에 명시가 없어 채택한 기본값).
 */

function pad2(value: number): string {
  return String(value).padStart(2, '0')
}

export function toDateKey(year: number, month: number, day: number): string {
  return `${year}-${pad2(month)}-${pad2(day)}`
}

interface DateKeyParts {
  year: number
  month: number
  day: number
}

function parseDateKey(dateKey: string): DateKeyParts {
  const [year, month, day] = dateKey.split('-').map(Number)
  return { year: year ?? 1970, month: month ?? 1, day: day ?? 1 }
}

/** `dateKey` 가 속한 요일(0=일 ~ 6=토). */
export function dayOfWeekIndex(dateKey: string): number {
  const { year, month, day } = parseDateKey(dateKey)
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay()
}

export function addDays(dateKey: string, delta: number): string {
  const { year, month, day } = parseDateKey(dateKey)
  const shifted = new Date(Date.UTC(year, month - 1, day + delta))
  return toDateKey(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, shifted.getUTCDate())
}

/** 월만 이동(일은 1로 고정 — 월간 뷰의 기준일은 항상 그 달 1일로 다룬다). */
export function addMonths(dateKey: string, delta: number): string {
  const { year, month } = parseDateKey(dateKey)
  const shifted = new Date(Date.UTC(year, month - 1 + delta, 1))
  return toDateKey(shifted.getUTCFullYear(), shifted.getUTCMonth() + 1, 1)
}

export function startOfMonth(dateKey: string): string {
  const { year, month } = parseDateKey(dateKey)
  return toDateKey(year, month, 1)
}

function daysInMonth(year: number, month: number): number {
  // month 는 1-based. day=0 은 "그 달의 0일" = 전달 말일이 아니라, Date.UTC(y, month, 0) 은
  // "m+1 월의 0일" = "m 월의 말일"이 된다(Date.UTC 의 month 파라미터가 0-based 이기 때문).
  return new Date(Date.UTC(year, month, 0)).getUTCDate()
}

export function endOfMonth(dateKey: string): string {
  const { year, month } = parseDateKey(dateKey)
  return toDateKey(year, month, daysInMonth(year, month))
}

/** `dateKey` 가 속한 주의 일요일. */
export function startOfWeek(dateKey: string): string {
  return addDays(dateKey, -dayOfWeekIndex(dateKey))
}

export interface CalendarGridCell {
  /** "yyyy-MM-dd" */
  date: string
  /** 월간 뷰에서 이웃 달의 여백 날짜인가(주간 뷰는 항상 true). */
  inCurrentPeriod: boolean
}

/**
 * 월간 그리드(6주 × 7일 = 42칸 고정) — 이웃 달 여백 포함. 서버 조회 범위(from/to)도 이 42일
 * 전체를 덮는다(그리드에 보이는 날짜는 다 조회할 수 있어야 한다 — 최대 62일 제약 안에 안전하게 든다).
 */
export function buildMonthGrid(dateKey: string): CalendarGridCell[] {
  const monthStart = startOfMonth(dateKey)
  const { month } = parseDateKey(monthStart)
  const gridStart = startOfWeek(monthStart)
  return Array.from({ length: 42 }, (_, index) => {
    const date = addDays(gridStart, index)
    return { date, inCurrentPeriod: parseDateKey(date).month === month }
  })
}

/** 주간 그리드(7일, 일~토 고정). */
export function buildWeekGrid(dateKey: string): CalendarGridCell[] {
  const gridStart = startOfWeek(dateKey)
  return Array.from({ length: 7 }, (_, index) => ({
    date: addDays(gridStart, index),
    inCurrentPeriod: true,
  }))
}

/** 오늘(브라우저 로컬 기준) 날짜 키. 화면의 "오늘" 버튼 전용 — 조회·판정 로직은 서버 `baseTime` 을 쓴다. */
export function todayDateKey(): string {
  const now = new Date()
  return toDateKey(now.getFullYear(), now.getMonth() + 1, now.getDate())
}
